import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Search,
  Plus,
  Server,
  Shield,
  DatabaseBackup,
  Activity,
  PlayCircle,
  PlugZap,
  AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";

const SOURCE_TYPES = [
  ["server", "Server"],
  ["backup_appliance", "Backup Appliance"],
  ["antivirus", "Anti Virus / EDR"],
  ["firewall", "Firewall"],
  ["siem", "SIEM / Log Platform"],
  ["cloud", "Cloud Platform"],
  ["identity_provider", "Identity Provider"],
  ["network_appliance", "Network Appliance"],
  ["other", "Other"],
];

const CONNECTION_MODES = [
  ["api", "API"],
  ["agent", "Agent"],
  ["syslog", "Syslog"],
  ["file_drop", "File Drop"],
  ["manual", "Manual"],
];

function iconFor(type) {
  if (type === "server") return Server;
  if (type === "backup_appliance") return DatabaseBackup;
  if (type === "antivirus") return Shield;
  if (type === "firewall") return Shield;
  return PlugZap;
}

function statusClass(status) {
  if (status === "active") return "bg-green-500/10 text-green-400 border-green-500/30";
  if (status === "error") return "bg-red-500/10 text-red-400 border-red-500/30";
  if (status === "paused") return "bg-yellow-500/10 text-yellow-400 border-yellow-500/30";
  return "bg-zinc-900 text-zinc-400 border-zinc-800";
}

export default function Integrations() {
  const [items, setItems] = useState([]);
  const [runs, setRuns] = useState([]);
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const [form, setForm] = useState({
    name: "",
    source_type: "server",
    vendor: "",
    connection_mode: "api",
    endpoint_url: "",
    client_id: "",
    description: "",
  });

  const load = async () => {
    const [sourcesRes, runsRes, statsRes] = await Promise.all([
      api.get("/integrations"),
      api.get("/integrations/harvest-runs"),
      api.get("/integrations/stats"),
    ]);
    setItems(sourcesRes.data || []);
    setRuns(runsRes.data || []);
    setStats(statsRes.data || null);
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;

    return items.filter((i) =>
      [i.name, i.vendor, i.source_type, i.connection_mode, i.description]
        .some((x) => String(x || "").toLowerCase().includes(q))
    );
  }, [items, search]);

  const resetForm = () => {
    setForm({
      name: "",
      source_type: "server",
      vendor: "",
      connection_mode: "api",
      endpoint_url: "",
      client_id: "",
      description: "",
    });
  };

  const createIntegration = async () => {
    if (!form.name.trim()) {
      toast.error("Source name is required");
      return;
    }

    setBusy(true);
    try {
      await api.post("/integrations", {
        ...form,
        vendor: form.vendor || null,
        endpoint_url: form.endpoint_url || null,
        client_id: form.client_id || null,
        description: form.description || null,
      });
      toast.success("Log source registered");
      setDialogOpen(false);
      resetForm();
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const runHarvest = async (integration) => {
    setBusy(true);
    try {
      await api.post("/integrations/harvest-runs", {
        integration_id: integration.id,
        mode: "manual",
        control_codes: [],
      });
      toast.success("Harvest run created");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="integrations-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="label-uppercase">Proactive Evidence</div>
          <h1 className="text-3xl tracking-tight font-medium mt-2 text-white">Integrations & Log Sources</h1>
          <p className="text-sm text-zinc-500 mt-1 max-w-3xl">
            Prepare CyberAssess to collect compliance evidence from authorized servers, backup appliances, anti-virus platforms, firewalls, and other systems with logs.
          </p>
        </div>

        <Button
          onClick={() => setDialogOpen(true)}
          className="bg-white text-black hover:bg-zinc-200"
          data-testid="add-integration"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Source
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
        <Stat label="Sources" value={stats?.total_sources || 0} />
        <Stat label="Active" value={stats?.active_sources || 0} />
        <Stat label="Configured" value={stats?.configured_sources || 0} />
        <Stat label="Harvest Runs" value={stats?.harvest_runs || 0} />
      </div>

      <div className="mt-6 rounded-md border border-cyan-500/20 bg-cyan-500/5 p-4">
        <div className="flex items-start gap-3">
          <Activity className="w-4 h-4 text-cyan-400 mt-0.5" />
          <div>
            <div className="text-sm text-white font-medium">Proactive Evidence Engine</div>
            <p className="text-xs text-zinc-400 mt-1 leading-relaxed">
              This module is designed for authorized, scoped, and auditable collection of compliance-relevant telemetry.
              It prepares the platform for automated evidence generation without exposing raw infrastructure details to ordinary users.
            </p>
          </div>
        </div>
      </div>

      <div className="mt-6 relative max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search log sources…"
          className="pl-10 bg-[#121212] border-zinc-800"
          data-testid="integration-search"
        />
      </div>

      <div className="mt-4 rounded-md border border-zinc-800 bg-[#121212] overflow-hidden">
        {filtered.length === 0 ? (
          <div className="p-12 text-center text-zinc-500 text-sm">
            <PlugZap className="w-8 h-8 mx-auto mb-3 text-zinc-700" />
            No integration sources configured yet.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#0A0A0A]">
              <tr>
                <th className="text-left px-4 py-3 label-uppercase">Source</th>
                <th className="text-left px-4 py-3 label-uppercase">Type</th>
                <th className="text-left px-4 py-3 label-uppercase">Mode</th>
                <th className="text-left px-4 py-3 label-uppercase">Status</th>
                <th className="text-left px-4 py-3 label-uppercase">Last Harvest</th>
                <th className="text-right px-4 py-3 label-uppercase">Actions</th>
              </tr>
            </thead>

            <tbody>
              {filtered.map((item) => {
                const Icon = iconFor(item.source_type);
                return (
                  <tr key={item.id} className="border-t border-zinc-800 hover:bg-zinc-900/40">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Icon className="w-4 h-4 text-zinc-500" />
                        <div>
                          <div className="text-white font-medium">{item.name}</div>
                          <div className="text-[10px] text-zinc-500">
                            {item.vendor || "No vendor"} {item.endpoint_url ? `· ${item.endpoint_url}` : ""}
                          </div>
                        </div>
                      </div>
                    </td>

                    <td className="px-4 py-3 text-zinc-300">
                      {SOURCE_TYPES.find(([v]) => v === item.source_type)?.[1] || item.source_type}
                    </td>

                    <td className="px-4 py-3">
                      <Badge className="bg-zinc-900 text-zinc-300 border-zinc-800 uppercase">
                        {item.connection_mode}
                      </Badge>
                    </td>

                    <td className="px-4 py-3">
                      <Badge className={statusClass(item.status)}>
                        {item.status}
                      </Badge>
                    </td>

                    <td className="px-4 py-3 text-xs text-zinc-500 mono">
                      {item.last_harvest_at ? item.last_harvest_at.slice(0, 19).replace("T", " ") : "Never"}
                    </td>

                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => runHarvest(item)}
                        disabled={busy}
                        className="inline-flex items-center gap-1 text-xs text-zinc-400 hover:text-white disabled:opacity-50"
                        data-testid={`run-harvest-${item.id}`}
                      >
                        <PlayCircle className="w-3.5 h-3.5" />
                        Run Harvest
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div className="mt-6 rounded-md border border-zinc-800 bg-[#121212] overflow-hidden">
        <div className="px-4 py-3 border-b border-zinc-800">
          <div className="label-uppercase">Recent Harvest Runs</div>
        </div>

        {runs.length === 0 ? (
          <div className="p-6 text-sm text-zinc-500">No harvest runs yet.</div>
        ) : (
          <div className="divide-y divide-zinc-800">
            {runs.slice(0, 10).map((run) => (
              <div key={run.id} className="px-4 py-3 flex items-start justify-between gap-3">
                <div>
                  <div className="text-sm text-white">{run.integration_name}</div>
                  <div className="text-xs text-zinc-500 mt-0.5">
                    {run.summary}
                  </div>
                </div>

                <div className="text-right shrink-0">
                  <Badge className="bg-green-500/10 text-green-400 border-green-500/30">
                    {run.status}
                  </Badge>
                  <div className="text-[10px] text-zinc-500 mono mt-1">
                    {run.completed_at?.slice(0, 19).replace("T", " ")}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#121212] border-zinc-800 text-white sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>Add Log Source</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label className="label-uppercase">Source Name</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Core Firewall, Backup Appliance, Endpoint AV"
                className="mt-1.5 bg-[#0A0A0A] border-zinc-800"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="label-uppercase">Source Type</Label>
                <Select value={form.source_type} onValueChange={(v) => setForm({ ...form, source_type: v })}>
                  <SelectTrigger className="mt-1.5 bg-[#0A0A0A] border-zinc-800">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121212] border-zinc-800 text-white">
                    {SOURCE_TYPES.map(([value, label]) => (
                      <SelectItem key={value} value={value}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="label-uppercase">Connection Mode</Label>
                <Select value={form.connection_mode} onValueChange={(v) => setForm({ ...form, connection_mode: v })}>
                  <SelectTrigger className="mt-1.5 bg-[#0A0A0A] border-zinc-800">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121212] border-zinc-800 text-white">
                    {CONNECTION_MODES.map(([value, label]) => (
                      <SelectItem key={value} value={value}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="label-uppercase">Vendor / Platform</Label>
              <Input
                value={form.vendor}
                onChange={(e) => setForm({ ...form, vendor: e.target.value })}
                placeholder="e.g. Fortinet, Sophos, Veeam, Windows Server, Linux"
                className="mt-1.5 bg-[#0A0A0A] border-zinc-800"
              />
            </div>

            <div>
              <Label className="label-uppercase">Endpoint / Connector URL</Label>
              <Input
                value={form.endpoint_url}
                onChange={(e) => setForm({ ...form, endpoint_url: e.target.value })}
                placeholder="Optional. Do not store secrets here."
                className="mt-1.5 bg-[#0A0A0A] border-zinc-800"
              />
            </div>

            <div>
              <Label className="label-uppercase">Client / Tenant Reference</Label>
              <Input
                value={form.client_id}
                onChange={(e) => setForm({ ...form, client_id: e.target.value })}
                placeholder="Optional client or tenant reference"
                className="mt-1.5 bg-[#0A0A0A] border-zinc-800"
              />
            </div>

            <div>
              <Label className="label-uppercase">Description</Label>
              <Textarea
                rows={3}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Describe what compliance-relevant evidence this source can provide."
                className="mt-1.5 bg-[#0A0A0A] border-zinc-800"
              />
            </div>

            <div className="rounded-md border border-yellow-500/20 bg-yellow-500/5 p-3 text-xs text-yellow-200 leading-relaxed">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                <div>
                  Store secrets only through a vault-backed connector in the next phase. This screen registers the source and prepares the evidence pipeline; it should not store raw passwords or API tokens.
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="pt-3">
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-zinc-800 text-zinc-300 hover:bg-zinc-900">
              Cancel
            </Button>
            <Button onClick={createIntegration} disabled={busy} className="bg-white text-black hover:bg-zinc-200">
              Save Source
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-md border border-zinc-800 bg-[#121212] p-5">
      <div className="label-uppercase">{label}</div>
      <div className="mt-3 text-3xl tracking-tight font-medium mono text-white">
        {value}
      </div>
    </div>
  );
}
